import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { SectionHeading } from "@/components/Atoms";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PackOpeningModal } from "@/components/PackOpeningModal";
import { toast } from "sonner";
import { InventoryIcon, InventoryName } from "@/components/InventoryIdentity";
import { inventoryVisual } from "@/lib/inventoryVisuals";
import { Coins, Mail, Shield, Receipt, CheckCircle2, Clock, XCircle, Package, Ticket, Sparkles, ArrowRight, Backpack, PackagePlus, Swords, Zap, Dices, Gift } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const STATUS_STYLE = {
  paid: { label: "Paid", cls: "text-emerald-300", Icon: CheckCircle2 },
  complete: { label: "Paid", cls: "text-emerald-300", Icon: CheckCircle2 },
  cancelled: { label: "Cancelled", cls: "text-red-300", Icon: XCircle },
  expired: { label: "Expired", cls: "text-white/40", Icon: XCircle },
};

const TOKEN_ICON = { talentshow_free_entry: Ticket, hq_enhancement: Sparkles, arena_token: Swords, pack_bonus_card: PackagePlus, reroll_token: Dices };
const TOKEN_USE = {
  talentshow_free_entry: { to: "/talentshow", label: "Use on a Talent Show entry" },
  hq_enhancement: { to: "/collection", label: "Use on a card's Enhance ticket" },
  reroll_token: { to: "/upgrade", label: "Use on a Forge reroll" },
};

function fmtBuff(secs) {
  if (secs <= 0) return null;
  const m = Math.floor(secs / 60), s = secs % 60;
  if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m left`;
  return m > 0 ? `${m}m left` : `${s}s left`;
}

function PurchaseHistory() {
  const { data: txns, isLoading } = useQuery({
    queryKey: ["payment-history"],
    queryFn: async () => (await api.get("/payments/history")).data,
  });
  const rows = txns || [];
  return (
    <div className="mt-8" data-testid="purchase-history">
      <h2 className="font-display text-xl text-white tracking-[0.08em] mb-3 flex items-center gap-2">
        <Receipt className="h-5 w-5 text-cyan-300" /> PURCHASE HISTORY
      </h2>
      {isLoading ? (
        <div className="hud-pill rounded-xl p-6 text-white/40 font-mono text-xs text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="hud-pill rounded-xl p-6 text-white/40 font-mono text-xs text-center" data-testid="purchase-history-empty">
          No real-money credit purchases yet.
        </div>
      ) : (
        <div className="hud-pill rounded-xl overflow-hidden divide-y divide-white/5" data-testid="purchase-history-list">
          {rows.map((t) => {
            const st = STATUS_STYLE[t.payment_status] || { label: "Pending", cls: "text-amber-300", Icon: Clock };
            return (
              <div key={t.id || t.order_id} data-testid={`purchase-row-${t.id || t.order_id}`} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <Coins className="h-4 w-4 text-amber-300 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-white text-sm truncate">{t.package_name || "Credit pack"}</div>
                  <div className="font-mono text-[10px] text-white/40">{t.created_at ? new Date(t.created_at).toLocaleString() : ""}</div>
                  {t.provider === "stripe" && t.order_id && t.status !== "complete" && <Link data-testid={`purchase-check-${t.order_id}`} className="text-xs text-cyan-300 underline" to={`/credits/return?order_id=${encodeURIComponent(t.order_id)}&provider=stripe`}>Check payment status</Link>}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-sm text-amber-300">+{Number(t.credits || 0).toLocaleString()}</div>
                  <div className="font-mono text-[10px] text-white/40">${Number(t.amount_usd || 0).toFixed(2)} {(t.currency || "usd").toUpperCase()}</div>
                </div>
                <div className={`flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.15em] shrink-0 ${st.cls}`}>
                  <st.Icon className="h-3.5 w-3.5" /> {st.label}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Inventory() {
  const qc = useQueryClient();
  const [opening, setOpening] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["inventory"], queryFn: async () => (await api.get("/inventory")).data, refetchInterval: 20000 });
  const open = useMutation({
    mutationFn: async (invId) => (await api.post(`/packs/inventory/${invId}/open`)).data,
    onSuccess: (d) => { setOpening({ cards: d.cards, packName: d.pack_name }); qc.invalidateQueries({ queryKey: ["inventory"] }); qc.invalidateQueries({ queryKey: ["my-cards"] }); },
    onError: (e) => toast.error(apiError(e, "Couldn't open pack")),
  });
  const usePackBonus = useMutation({
    mutationFn: async () => (await api.post(`/inventory/use/pack-bonus`)).data,
    onSuccess: () => { toast.success("Bountiful Charm activated — +1 card per pack for 1 hour!"); qc.invalidateQueries({ queryKey: ["inventory"] }); },
    onError: (e) => toast.error(apiError(e, "Couldn't activate the charm")),
  });
  if (isLoading) return <div className="hud-pill rounded-xl p-8 text-white/40 font-mono text-xs text-center">Loading inventory…</div>;
  const tokens = data?.tokens || [];
  const packs = data?.packs || [];
  const packBonusLeft = data?.buffs?.pack_bonus_seconds_left || 0;
  const ownedTokens = tokens.filter((t) => t.qty > 0);
  const empty = ownedTokens.length === 0 && packs.length === 0;

  return (
    <div className="space-y-8" data-testid="inventory-panel">
      {/* Tokens */}
      <div>
        <h2 className="font-display text-xl text-white tracking-[0.08em] mb-1 flex items-center gap-2"><Ticket className="h-5 w-5 text-orange-300" /> TOKENS</h2>
        <p className="text-white/45 text-xs mb-4">Consumable items you can spend across Playden — or trade away like credits and cards.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {tokens.map((t) => {
            const Icon = TOKEN_ICON[t.type] || Sparkles;
            const use = TOKEN_USE[t.type];
            const owned = t.qty > 0;
            return (
              <div key={t.type} data-testid={`inventory-token-${t.type}`}
                className={`hud-pill rounded-2xl p-4 flex items-start gap-4 transition-colors ${owned ? "" : "opacity-55"}`}
                style={{ borderColor: owned ? `${t.color}44` : undefined }}>
                <div className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0 relative" style={{ backgroundColor: `${t.color}22`, border: `1px solid ${t.color}55` }}>
                  <InventoryIcon item={t} className="h-6 w-6" />
                  <span data-testid={`inventory-token-qty-${t.type}`} className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 rounded-full bg-black border text-[11px] font-mono font-bold flex items-center justify-center" style={{ borderColor: t.color, color: t.color }}>{t.qty}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <InventoryName item={t} className="block text-lg" />
                  <div className="text-[12px] text-white/55 mt-0.5 leading-relaxed">{t.desc}</div>
                  {t.type === "pack_bonus_card" ? (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {packBonusLeft > 0 && (
                        <span data-testid="pack-bonus-active" className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.15em] rounded-full px-2 py-0.5 bg-lime-400/15 text-lime-300 border border-lime-400/30">
                          <Zap className="h-3 w-3" /> Active · {fmtBuff(packBonusLeft)}
                        </span>
                      )}
                      <Button size="sm" data-testid="pack-bonus-activate" disabled={!owned || usePackBonus.isPending}
                        onClick={() => usePackBonus.mutate()}
                        className="h-7 bg-lime-400 text-black hover:bg-lime-300 disabled:opacity-40 text-xs font-medium">
                        {usePackBonus.isPending ? "Activating…" : packBonusLeft > 0 ? "Extend +1h" : "Activate"}
                      </Button>
                    </div>
                  ) : use && (
                    <Link to={use.to} data-testid={`inventory-token-use-${t.type}`} className="mt-2 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] hover:gap-2 transition-all" style={{ color: t.color }}>
                      {use.label} <ArrowRight className="h-3 w-3" />
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Packs */}
      <div>
        <h2 className="font-display text-xl text-white tracking-[0.08em] mb-1 flex items-center gap-2"><Package className="h-5 w-5 text-fuchsia-300" /> UNOPENED PACKS</h2>
        <p className="text-white/45 text-xs mb-4">Sealed packs you've won or been gifted — rip them to roll new cards.</p>
        {packs.length === 0 ? (
          <div className="hud-pill rounded-xl p-6 text-white/40 font-mono text-xs text-center" data-testid="inventory-packs-empty">
            No unopened packs. Win them from Commissions, the Gauntlet or claim codes.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {packs.map((p) => (
              <div key={p.id} data-testid={`inventory-pack-${p.id}`} className="rounded-xl border border-fuchsia-400/30 bg-fuchsia-400/[0.05] p-3 flex items-center gap-3">
                <div className="h-12 w-12 rounded-lg bg-fuchsia-500/15 border border-fuchsia-400/30 flex items-center justify-center shrink-0 overflow-hidden">
                  {p.pack_image ? <img src={p.pack_image} alt="" className="w-full h-full object-cover" /> : <Package className="h-6 w-6 text-fuchsia-300" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-white text-sm font-medium truncate">{p.pack_name || "Card Pack"}</div>
                  <div className="font-mono text-[10px] text-white/40">{p.source ? `Won from ${p.source}` : "Sealed pack"}</div>
                </div>
                <Button size="sm" data-testid={`inventory-open-pack-${p.id}`} disabled={open.isPending} onClick={() => open.mutate(p.id)} className="bg-fuchsia-400 text-black hover:bg-fuchsia-300 shrink-0">Open</Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {empty && (
        <div className="hud-pill rounded-2xl p-8 text-center" data-testid="inventory-empty">
          <Backpack className="h-9 w-9 text-white/25 mx-auto" />
          <div className="font-display text-xl text-white mt-3 tracking-[0.05em]">YOUR INVENTORY IS EMPTY</div>
          <p className="text-white/45 text-sm mt-2 max-w-md mx-auto">Earn tokens and packs from <Link to="/path" className="text-yellow-300">the Path</Link> (quests &amp; commissions) and <Link to="/redeem" className="text-amber-300">claim codes</Link>.</p>
        </div>
      )}

      {opening && <PackOpeningModal cards={opening.cards} packName={opening.packName} onClose={() => setOpening(null)} />}
    </div>
  );
}

function GiftCreditsDialog({ tradeable, onDone }) {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState("");
  const [amount, setAmount] = useState("");
  const amt = parseInt(amount, 10) || 0;
  const mut = useMutation({
    mutationFn: async () => (await api.post("/credits/gift", { to_user: to.trim(), amount: amt })).data,
    onSuccess: (d) => {
      toast.success(`Sent ${d.amount.toLocaleString()} credits to ${d.to}`);
      setOpen(false); setTo(""); setAmount("");
      onDone?.();
    },
    onError: (e) => toast.error(apiError(e)),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="mt-3 w-full gap-1.5" data-testid="open-gift-credits-btn" disabled={tradeable <= 0}>
          <Gift className="h-3.5 w-3.5" /> Gift credits
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[hsl(220_18%_10%)] border-white/10">
        <DialogHeader>
          <DialogTitle className="font-display tracking-[0.06em] flex items-center gap-2"><Gift className="h-4 w-4 text-emerald-300" /> Gift Credits to a Player</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-white/60">
            Sends your <span className="text-amber-300">transferable</span> credits to another player.
            Gift Credits in your balance can never be sent — only you can spend those on yourself.
          </p>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Recipient (display name)</label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Player name" className="mt-1 bg-black/30 border-white/10" data-testid="gift-recipient-input" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Amount</label>
            <Input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" className="mt-1 bg-black/30 border-white/10 font-mono" data-testid="gift-amount-input" />
            <div className="text-[11px] text-white/40 mt-1">You can send up to {tradeable.toLocaleString()} transferable credits.</div>
          </div>
        </div>
        <DialogFooter>
          <Button
            className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold gap-1.5"
            data-testid="confirm-gift-credits-btn"
            disabled={mut.isPending || !to.trim() || amt < 1 || amt > tradeable}
            onClick={() => mut.mutate()}
          >
            <Gift className="h-4 w-4" /> {mut.isPending ? "Sending…" : `Send ${amt > 0 ? amt.toLocaleString() : ""} credits`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProfileCreditsCard({ user, refresh }) {
  const total = user.credits ?? 0;
  const gift = user.gift_credits ?? 0;
  const tradeable = user.tradeable_credits ?? Math.max(0, total - gift);
  return (
    <div className="hud-pill rounded-xl p-4 flex flex-col items-center justify-center" data-testid="profile-credits-card">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300/80">Credits</div>
      <div className="font-display text-4xl text-amber-300 mt-1" data-testid="profile-total-credits">{total.toLocaleString()}</div>
      <Coins className="h-5 w-5 text-amber-300 mt-2" />
      <div className="w-full mt-3 pt-3 border-t border-white/10 space-y-1.5 text-xs">
        <div className="flex items-center justify-between">
          <span className="text-white/50 flex items-center gap-1"><ArrowRight className="h-3 w-3" /> Transferable</span>
          <span className="font-mono text-white tabular-nums" data-testid="profile-tradeable-credits">{tradeable.toLocaleString()}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-emerald-300/80 flex items-center gap-1"><Gift className="h-3 w-3" /> Gift Credits</span>
          <span className="font-mono text-emerald-300 tabular-nums" data-testid="profile-gift-credits">{gift.toLocaleString()}</span>
        </div>
        <div className="text-[10px] text-white/35 leading-snug pt-1">Gift Credits are spendable anywhere on yourself but can never be given to another player.</div>
      </div>
      <GiftCreditsDialog tradeable={tradeable} onDone={refresh} />
    </div>
  );
}

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const { data: cards } = useQuery({ queryKey: ["my-cards"], queryFn: async () => (await api.get("/cards/mine")).data });
  const tally = (cards || []).reduce((acc, c) => { acc[c.rarity] = (acc[c.rarity] || 0) + 1; return acc; }, {});
  if (!user) return null;
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <SectionHeading eyebrow="Profile" title="YOUR ACCOUNT" />

      <div className="hud-pill rounded-2xl p-6 grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-cyan-400 to-emerald-400 flex items-center justify-center text-black font-display text-2xl">
              {user.display_name?.slice(0, 1).toUpperCase()}
            </div>
            <div>
              <div className="font-display text-2xl text-white tracking-[0.06em]">{user.display_name}</div>
              <div className="text-sm text-white/50 flex items-center gap-1"><Mail className="h-3 w-3" /> {user.email}</div>
              {user.role === "admin" && <div className="text-emerald-300 text-xs mt-1 inline-flex items-center gap-1"><Shield className="h-3 w-3" /> Admin</div>}
            </div>
          </div>
          <div className="font-mono text-xs text-white/40">Member since {new Date(user.created_at).toLocaleDateString()}</div>
          <div className="font-mono text-[10px] text-white/30 break-all">USER ID: {user.id}</div>
        </div>
        <ProfileCreditsCard user={user} refresh={refresh} />
      </div>

      <Tabs defaultValue="overview" className="mt-8">
        <TabsList className="bg-[hsl(220_18%_12%)] border border-white/10">
          <TabsTrigger value="overview" data-testid="profile-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="inventory" data-testid="profile-tab-inventory" className="flex items-center gap-1.5"><Backpack className="h-3.5 w-3.5" /> Inventory</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <h2 className="font-display text-xl text-white tracking-[0.08em] mb-3">COLLECTION TALLY</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {["common", "uncommon", "rare", "epic", "legendary", "goddess"].map((r) => (
              <div key={r} className={`rarity-${r} hud-pill rounded-xl p-3 text-center relative overflow-hidden`}>
                <div className="foil-overlay" />
                <div className="font-mono text-[9px] uppercase tracking-[0.3em]" style={{ color: "var(--label)" }}>{r}</div>
                <div className="font-display text-3xl text-white mt-1">{tally[r] || 0}</div>
              </div>
            ))}
          </div>
          <PurchaseHistory />
        </TabsContent>

        <TabsContent value="inventory" className="mt-6">
          <Inventory />
        </TabsContent>
      </Tabs>
    </div>
  );
}
